import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { AIPreset, AIPresetCategory, AIProductStagingRule, AIVirtualModelRule } from '@zakeke/zakeke-configurator-react';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { useFireUserFileUpload } from './hooks';
import { useDialogManager } from '../dialogs/Dialogs';
import ErrorDialog from '../dialogs/ErrorDialog';

export type PresetTypeProductStaging = 'mood' | 'background' | 'effect' | 'surface';
export type PresetTypeVirtualTryOn = 'person' | 'pose' | 'background' | 'effect';
export type PresetType = PresetTypeProductStaging | PresetTypeVirtualTryOn;



/**
 * Structure for uploaded images for each preset type
 */
export type UploadedImage = {
	imageUrl: string;
	sourceImageUrl: string | null;
};

export type ImageUrlState = {
	selectedUrl: string;
	uploadedUrls: UploadedImage[];
	isUploading?: boolean;
};

export type ImageUrlByPresetType = {
	[K in PresetType]?: ImageUrlState;
};

// Unified panel data state structure
export type PanelDataState = {
	presets: AIPreset[];
	categories?: AIPresetCategory[];
	isLoading: boolean;
};

/**
 * Convert an image URL to base64
 */
const urlToBase64 = async (url: string): Promise<string> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'Anonymous';
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.drawImage(img, 0, 0);
				const dataURL = canvas.toDataURL('image/png');
				resolve(dataURL);
			} else {
				reject(new Error('Failed to get canvas context'));
			}
		};
		img.onerror = () => {
			reject(new Error('Failed to load image'));
		};
		img.src = url;
	});
};

/**
 * Hook to fetch and convert AI Presets data using useZakeke
 */
const useFetchAIPresets = () => {
	const { getAIPresetCategories, getAIPresetPresets } = useZakeke();

	// Use refs to stabilize function references and prevent loadPanelData effect re-triggers
	const getAIPresetPresetsRef = useRef(getAIPresetPresets);
	getAIPresetPresetsRef.current = getAIPresetPresets;
	const getAIPresetCategoriesRef = useRef(getAIPresetCategories);
	getAIPresetCategoriesRef.current = getAIPresetCategories;

	const fetchPresets = useCallback(
		async (presetType: PresetType): Promise<AIPreset[]> => {
			return await getAIPresetPresetsRef.current(presetType);
		},
		[]
	);

	const fetchCategories = useCallback(
		async (categoryType: PresetType): Promise<AIPresetCategory[]> => {
			return await getAIPresetCategoriesRef.current(categoryType);
		},
		[]
	);

	return {
		fetchPresets,
		fetchCategories,
	};
};

/**
 * Custom hook to access AI Product Staging data asynchronously
 * Provides typed access to all configuration data for the AI Studio feature
 *
 * Data sources:
 * - Moods: data.productStaging.ts (MOOD_PRESETS)
 * - Backgrounds: data.productStaging.ts (BACKGROUND_PRESETS, BACKGROUND_CATEGORIES)
 * - Surfaces: data.productStaging.ts (SURFACE_PRESETS)
 * - Effects: data.productStaging.ts (EFFECT_PRESETS, EFFECT_CATEGORIES)
 * - Models, Poses, Ratios, Tools: data.json (legacy)
 *
 * @returns Object containing data, loading state, and error state
 * @example
 * const { data, isLoading, error } = useAIProductStagingData();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!data) return null;
 *
 * // Use data.moods, data.backgrounds, etc.
 */
export const useAIProductStagingData = (initialMode?: 'product-staging' | 'virtual-tryon') => {
	// Get Zakeke context
	const zakeke = useZakeke();
	const {
		product,
		generateAIProductStaging,
		generateAIVirtualTryon,
		getOnlineScreenshot,
		createImage,
		getApplicableAIStagingRules,
		getApplicableAIVirtualModelRules,
		T_static
	} = zakeke;

	// Create T object compatible with customizer API
	const T = useMemo(() => ({
		_: T_static,
		_d: T_static, // In configurator we only have static translations
		_withOptions: T_static,
		_dWithOptions: T_static,
	}), [T_static]);

	// Base data state (always loaded)
	const [currentAssetId] = useState(1); // SAREBBE L'immagine da customizer - questo andrebbe passato all'hook da fuori

	// Add hooks for upload functionality
	const fireUserFileUpload = useFireUserFileUpload();

	// State to switch between 'product-staging' and 'virtual-tryon'
	const [modalType, setModalType] = useState<'product-staging' | 'virtual-tryon'>(initialMode ?? 'product-staging');

	// Use the fetch hook to get data fetching functions
	const { fetchPresets, fetchCategories } = useFetchAIPresets();
	const { showDialog, closeDialog } = useDialogManager();

	const [panelData, setPanelData] = useState<{
		mood: PanelDataState;
		person: PanelDataState;
		pose: PanelDataState;
		background: PanelDataState;
		surface: PanelDataState;
		effect: PanelDataState;
	}>({
		mood: { presets: [], categories: [], isLoading: false },
		person: { presets: [], categories: [], isLoading: false },
		pose: { presets: [], categories: [], isLoading: false },
		background: { presets: [], categories: [], isLoading: false },
		surface: { presets: [], categories: [], isLoading: false },
		effect: { presets: [], categories: [], isLoading: false },
	});

	// Modified images array
	const [modifiedImages, setModifiedImages] = useState<Array<string>>([]);

	// Loading state for generation
	const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);

	// State management
	const [hasGenerated, setHasGenerated] = useState(false);
	const [activePanel, setActivePanel] = useState<string | null>(null);
	const [prompt, setPrompt] = useState('');

	// Selected options state
	const [selectedBackground, setSelectedBackground] = useState<number | null>(null);
	const [selectedPerson, setSelectedPerson] = useState<number | null>(null);
	const [selectedPose, setSelectedPose] = useState<number | null>(null);
	const [selectedSurface, setSelectedSurface] = useState<number | null>(null);
	const [selectedMood, setSelectedMood] = useState<number | null>(null);
	const [selectedEffect, setSelectedEffect] = useState<number | null>(null);

	// (randomization moved after filteredPanelData)

	// Image URL state (for user uploaded images) - NEW STRUCTURE
	const [imageUrl, setImageUrl] = useState<ImageUrlByPresetType>({});

	// Input image state (for "Use as Input" feature - uses a generated image as input for next generation)
	const [inputImage, setInputImage] = useState<string | null>(null);

	// AI Staging Rules state (product-specific filtering)
	const [aiStagingRules, setAiStagingRules] = useState<AIProductStagingRule | null>(null);
	const [aiVtoRules, setAiVtoRules] = useState<AIVirtualModelRule | null>(null);
	const [allowCustomBackground, setAllowCustomBackground] = useState(true);
	const [rulesLoaded, setRulesLoaded] = useState(false);

	// Use refs for API functions to prevent effect re-triggers on function reference changes
	const getApplicableAIStagingRulesRef = useRef(getApplicableAIStagingRules);
	getApplicableAIStagingRulesRef.current = getApplicableAIStagingRules;
	const getApplicableAIVirtualModelRulesRef = useRef(getApplicableAIVirtualModelRules);
	getApplicableAIVirtualModelRulesRef.current = getApplicableAIVirtualModelRules;

	/**
	 * Load data for a specific panel (presets and categories if applicable)
	 */
	const loadPanelData = useCallback(
		async (panelType: PresetType) => {
			// Set loading state
			setPanelData((prev) => ({
				...prev,
				[panelType]: { ...prev[panelType], isLoading: true },
			}));

			try {
				// Check if this panel type has categories (background and effect)

				// Load both presets and categories in parallel
				const [loadedPresets, loadedCategories] = await Promise.all([
					fetchPresets(panelType),
					fetchCategories(panelType),
				]);

				setPanelData((prev) => ({
					...prev,
					[panelType]: {
						presets: loadedPresets,
						categories: loadedCategories,
						isLoading: false,
					},
				}));
			} catch (err) {
				console.error(`Error loading ${panelType} data:`, err);
				// Reset loading state on error
				setPanelData((prev) => ({
					...prev,
					[panelType]: { ...prev[panelType], isLoading: false },
				}));
			}
		},
		[fetchPresets, fetchCategories]
	);

	/**
	 * Load all panel data on mount
	 */
	useEffect(() => {
		const panelTypes: Array<PresetType> = ['mood', 'person', 'pose', 'background', 'surface', 'effect'];

		panelTypes.forEach((panelType) => {
			loadPanelData(panelType);
		});
	}, [loadPanelData]);

	/**
	 * Fetch AI Staging Rules when modal type is product-staging and product is available
	 */
	useEffect(() => {
		let cancelled = false;
		setRulesLoaded(false);
		if (modalType === 'product-staging' && product?.id) {
			getApplicableAIStagingRulesRef.current({
				productID: product.id,
				printTypeID: undefined,
			})
				.then((rules) => {
					if (cancelled) return;
					setAiStagingRules(rules);
					setAllowCustomBackground(rules?.allowCustomBackground ?? true);
					setRulesLoaded(true);
				})
				.catch((error) => {
					if (cancelled) return;
					console.error('Error fetching AI Staging Rules:', error);
					setAiStagingRules(null);
					setAllowCustomBackground(true);
					setRulesLoaded(true);
				});
		} else if (modalType === 'virtual-tryon' && product?.id) {
			getApplicableAIVirtualModelRulesRef.current({
				productID: product.id,
				printTypeID: undefined,
			})
				.then((rules: AIVirtualModelRule | null) => {
					if (cancelled) return;
					setAiVtoRules(rules);
					setAiStagingRules(null);
					setAllowCustomBackground(rules?.allowCustomBackground ?? true);
					setRulesLoaded(true);
				})
				.catch((error: unknown) => {
					if (cancelled) return;
					console.error('Error fetching AI VTO Rules:', error);
					setAiVtoRules(null);
					setAiStagingRules(null);
					setAllowCustomBackground(true);
					setRulesLoaded(true);
				});
		} else if (modalType === 'virtual-tryon') {
			setAiVtoRules(null);
			setAiStagingRules(null);
			setAllowCustomBackground(true);
			setRulesLoaded(true);
		}
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalType, product?.id]);

	/**
	 * Set active panel (data is already loaded on mount)
	 */
	const handleSetActivePanel = useCallback((panelId: string | null) => {
		setActivePanel(panelId);
	}, []);

	// Handlers
	const handleSelect = useCallback((type: string, value: number | null) => {
		switch (type) {
			case 'background':
				setSelectedBackground(value);
				if (value !== null) {
					setImageUrl((prev) => ({
						...prev,
						background: { selectedUrl: '', uploadedUrls: prev.background?.uploadedUrls || [] },
					}));
				}
				break;
			case 'person':
				setSelectedPerson(value);
				if (value !== null) {
					setImageUrl((prev) => ({
						...prev,
						person: { selectedUrl: '', uploadedUrls: prev.person?.uploadedUrls || [] },
					}));
				}
				break;
			case 'pose':
				setSelectedPose(value);
				break;
			case 'surface':
				setSelectedSurface(value);
				break;
			case 'mood':
				setSelectedMood(value);
				break;
			case 'effect':
				setSelectedEffect(value);
				break;
		}
	}, []);

	const handleGenerate = useCallback(async () => {
		if (isLoadingGenerate) return;

		// Show loader immediately
		setIsLoadingGenerate(true);

		// Use inputImage if available, otherwise take screenshot from 3D view
		let selectedImage = '';

		if (inputImage) {
			// Use the input image from "Use as Input" feature
			selectedImage = inputImage;
			console.log('[Generation] Using input image:', inputImage);
		} else {
			// Take screenshot from 3D view
			try {
				const screenshotResult = await getOnlineScreenshot(512, 512, false);
				if (screenshotResult?.originalUrl) {
					selectedImage = screenshotResult.originalUrl;
				}
			} catch (error) {
				console.error('Error taking 3D screenshot:', error);
				setIsLoadingGenerate(false); // Reset loading state on error
				showDialog(
					'error',
					<ErrorDialog
						error={T._('Failed to capture 3D view')}
						onCloseClick={() => closeDialog('error')}
					/>
				);
				return;
			}
		}

		if (!selectedImage) {
			setIsLoadingGenerate(false); // Reset loading state if no image
			return;
		}
		try {
			// Convert image to base64 if URL is not empty
			let productImageBase64 = selectedImage;
			if (selectedImage && !selectedImage.startsWith('data:')) {
				productImageBase64 = await urlToBase64(selectedImage);
			}

			let response: { imageUrl: string };
			// TODO - in caso di virtual try on e  backgroundImageUrl vuoto, passare utl di backgroundPreset,
			// TODO -  fare meccanismo per permettere di caricare immagine persona.
			if (modalType === 'virtual-tryon') {
				const generationData = {
					clothingImageUrl: productImageBase64 ?? '',
					backgroundType: selectedBackground ? 'preset' : 'none',
					backgroundPreset: selectedBackground || undefined,
					backgroundImageUrl: imageUrl?.background?.selectedUrl || '',
					effectPreset: selectedEffect || undefined,
					posePreset: selectedPose || undefined,
					personPreset: selectedPerson || undefined,
					personImageUrl: imageUrl?.person?.selectedUrl || undefined,
					customPrompt: prompt.trim() || undefined,
				};

				response = await generateAIVirtualTryon(generationData);
			} else {
				const generationData = {
					productImageUrl: productImageBase64 ?? '',
					backgroundType: selectedBackground ? 'preset' : 'none',
					backgroundPreset: selectedBackground || undefined,
					backgroundImageUrl: imageUrl?.background?.selectedUrl || '',
					surfacePreset: selectedSurface || undefined,
					moodPreset: selectedMood || undefined,
					effectPreset: selectedEffect || undefined,
					customPrompt: prompt.trim() || undefined,
				};

				response = await generateAIProductStaging(generationData);
			}

			// Append new image to existing ones, keeping max 4 images
			setModifiedImages((prev) => {
				const newImages = [...prev, response.imageUrl];
				return newImages.slice(-4); // Keep only last 4 images
			});

			setHasGenerated(true);
			setActivePanel(null);
			// Reset input image after generation to show the result
			setInputImage(null);
		} catch (error) {
			console.error('Generation error:', error);
			showDialog(
				'error',
				<ErrorDialog
					error={(error as Error)?.message || T._('Generic Error')}
					onCloseClick={() => closeDialog('error')}
				/>
			);
		} finally {
			setIsLoadingGenerate(false);
		}
	}, [
		selectedBackground,
		selectedSurface,
		selectedMood,
		selectedEffect,
		prompt,
		isLoadingGenerate,
		getOnlineScreenshot,
		selectedPose,
		selectedPerson,
		generateAIProductStaging,
		generateAIVirtualTryon,
		modalType,
		showDialog,
		closeDialog,
		imageUrl?.background?.selectedUrl,
		imageUrl?.person?.selectedUrl,
		inputImage,
		T,
	]);

	/**
	 * Check if a preset type supports user upload
	 * @param presetType - The preset type to check
	 * @returns true if the preset type supports upload (person or background)
	 */
	const hasUserUpload = useCallback((presetType: PresetType): boolean => {
		return presetType === 'person' || presetType === 'background';
	}, []);

	/**
	 * Handle user file upload for background or person presets
	 * @param presetType - The type of preset to upload for (background or person)
	 */
	const handleUpload = useCallback(
		(presetType: PresetType) => {
			fireUserFileUpload(async (file: File) => {
				setImageUrl((prev) => {
					const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
					return {
						...prev,
						[presetType]: {
							...currentState,
							isUploading: true,
						},
					};
				});

				try {
					const uploadedImageResult = await createImage(file);

					// Handle uploaded image: set imageUrl and clear selected preset
					if (uploadedImageResult?.choiceUrl) {
						const uploadedImage: UploadedImage = {
							imageUrl: uploadedImageResult.choiceUrl,
							sourceImageUrl: null,
						};

						// Update imageUrl state for both background and person
						setImageUrl((prev) => {
							const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
							return {
								...prev,
								[presetType]: {
									selectedUrl: uploadedImageResult.choiceUrl,
									uploadedUrls: [...currentState.uploadedUrls, uploadedImage],
									isUploading: false,
								},
							};
						});

						// Clear selected preset when user uploads an image
						if (presetType === 'background') {
							setSelectedBackground(null);
						} else if (presetType === 'person') {
							setSelectedPerson(null);
						}
					}
				} catch (error) {
					setImageUrl((prev) => ({
						...prev,
						[presetType]: { ...prev[presetType], isUploading: false },
					}));
					console.error(`Error uploading image for ${presetType}:`, error);
				}
			}, 'image/*');
		},
		[fireUserFileUpload, createImage]
	);

	/**
	 * Handle webcam capture for person preset
	 * Processes captured image from webcam
	 */
	const handleWebcamCapture = useCallback(
		async (file: File) => {
			const presetType: PresetType = 'person';

			// Set loading state
			setImageUrl((prev) => {
				const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
				return {
					...prev,
					[presetType]: {
						...currentState,
						isUploading: true,
					},
				};
			});

			try {
				const uploadedImageResult = await createImage(file);

				if (uploadedImageResult?.choiceUrl) {
					const uploadedImage: UploadedImage = {
						imageUrl: uploadedImageResult.choiceUrl,
						sourceImageUrl: null,
					};

					setImageUrl((prev) => {
						const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
						return {
							...prev,
							[presetType]: {
								selectedUrl: uploadedImageResult.choiceUrl,
								uploadedUrls: [...currentState.uploadedUrls, uploadedImage],
								isUploading: false,
							},
						};
					});

					// Clear selected person preset when webcam capture succeeds
					setSelectedPerson(null);
				}
			} catch (error) {
				setImageUrl((prev) => {
					const currentState = prev[presetType] || { selectedUrl: '', uploadedUrls: [] };
					return {
						...prev,
						[presetType]: { ...currentState, isUploading: false },
					};
				});
				console.error(`Error uploading webcam capture for ${presetType}:`, error);
			}
		},
		[createImage]
	);

	/**
	 * Handle deletion of a variant from the modifiedImages array
	 */
	const handleDeleteVariant = useCallback((variantId: number) => {
		setModifiedImages((prev) => {
			const newImages = [...prev];
			newImages.splice(variantId, 1);
			// If no images left, return to input state
			if (newImages.length === 0) {
				setHasGenerated(false);
			}
			return newImages;
		});
	}, []);

	/**
	 * Handle "Back to input" - return to input state while keeping generated images
	 */
	const handleBackToInput = useCallback(() => {
		setHasGenerated(false);
		setInputImage(null);
	}, []);

	/**
	 * Filter panelData based on AI Staging Rules (product-specific filtering)
	 */
	const filteredPanelData = useMemo(() => {
		// Don't expose any presets until rules are fully loaded to prevent tab flash
		if (!rulesLoaded) {
			return {
				mood: { ...panelData.mood, presets: [], categories: [] },
				person: { ...panelData.person, presets: [], categories: [] },
				pose: { ...panelData.pose, presets: [], categories: [] },
				background: { ...panelData.background, presets: [], categories: [] },
				surface: { ...panelData.surface, presets: [], categories: [] },
				effect: { ...panelData.effect, presets: [], categories: [] },
			};
		}

		// Apply Product Staging rules
		if (modalType === 'product-staging' && aiStagingRules) {
			const { backgroundCategoryIDs, surfacePresetIDs, moodPresetIDs, effectPresetIDs } = aiStagingRules;

			return {
				...panelData,
				background: {
					...panelData.background,
					categories: panelData.background.categories?.filter((cat) =>
						backgroundCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.background.presets.filter(
						(preset) => preset.categoryId !== null && backgroundCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				surface: {
					...panelData.surface,
					presets: panelData.surface.presets.filter((preset) => surfacePresetIDs.includes(Number(preset.id))),
				},
				mood: {
					...panelData.mood,
					presets: panelData.mood.presets.filter((preset) => moodPresetIDs.includes(Number(preset.id))),
				},
				effect: {
					...panelData.effect,
					presets: panelData.effect.presets.filter((preset) => effectPresetIDs.includes(Number(preset.id))),
				},
			};
		}

		// Apply Virtual Try-on rules
		if (modalType === 'virtual-tryon' && aiVtoRules) {
			const { backgroundCategoryIDs, effectPresetIDs, modelPresetIDs, poseCategoryIDs } = aiVtoRules;

			return {
				...panelData,
				person: {
					...panelData.person,
					presets: panelData.person.presets.filter((preset) => modelPresetIDs.includes(Number(preset.id))),
				},
				pose: {
					...panelData.pose,
					categories: panelData.pose.categories?.filter((cat) =>
						poseCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.pose.presets.filter(
						(preset) => preset.categoryId !== null && poseCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				background: {
					...panelData.background,
					categories: panelData.background.categories?.filter((cat) =>
						backgroundCategoryIDs.includes(Number(cat.id))
					),
					presets: panelData.background.presets.filter(
						(preset) => preset.categoryId !== null && backgroundCategoryIDs.includes(Number(preset.categoryId))
					),
				},
				effect: {
					...panelData.effect,
					presets: panelData.effect.presets.filter((preset) => effectPresetIDs.includes(Number(preset.id))),
				},
			};
		}

		return panelData;
	}, [panelData, aiStagingRules, aiVtoRules, modalType, rulesLoaded]);

	useEffect(() => {
		if (!rulesLoaded) return;

		const pickRandom = (presets: AIPreset[]): number | null => {
			if (presets.length === 0) return null;
			return presets[Math.floor(Math.random() * presets.length)].id;
		};

		if (modalType === 'product-staging') {
			if (filteredPanelData.background.presets.length === 0) return;
			setSelectedBackground(pickRandom(filteredPanelData.background.presets));
			setSelectedSurface(pickRandom(filteredPanelData.surface.presets));
			setSelectedMood(pickRandom(filteredPanelData.mood.presets));
			setSelectedEffect(pickRandom(filteredPanelData.effect.presets));
		} else if (modalType === 'virtual-tryon') {
			if (filteredPanelData.person.presets.length === 0) return;
			setSelectedPerson(pickRandom(filteredPanelData.person.presets));
			setSelectedPose(pickRandom(filteredPanelData.pose.presets));
			setSelectedBackground(pickRandom(filteredPanelData.background.presets));
			setSelectedEffect(pickRandom(filteredPanelData.effect.presets));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rulesLoaded, modalType, filteredPanelData]);

	return {
		// Unified panel data (filtered based on AI Staging Rules)
		panelData: filteredPanelData,

		// Legacy accessors for backward compatibility (use filtered data)
		mood: filteredPanelData.mood,
		person: filteredPanelData.person,
		pose: filteredPanelData.pose,
		background: filteredPanelData.background,
		surface: filteredPanelData.surface,
		effect: filteredPanelData.effect,

		// Loading states
		isLoadingPreset: Object.values(panelData).some((panel) => panel.isLoading) || !rulesLoaded,
		isLoadingGenerate,

		// Asset state
		currentAssetId,
		hasGenerated,
		setHasGenerated,
		activePanel,
		setActivePanel: handleSetActivePanel,
		prompt,
		setPrompt,

		// Selected options
		selectedBackground,
		setSelectedBackground,
		selectedPerson,
		setSelectedPerson,
		selectedPose,
		setSelectedPose,
		selectedSurface,
		setSelectedSurface,
		selectedMood,
		setSelectedMood,
		selectedEffect,
		setSelectedEffect,

		// TODO - ricontrollare - New image URL structure
		imageUrl,
		setImageUrl,

		// Input image for "Use as Input" feature
		inputImage,
		setInputImage,

		// Handlers
		handleSelect,
		handleGenerate,
		handleUpload,
		handleDeleteVariant,
		handleBackToInput,
		handleWebcamCapture,
		hasUserUpload,

		// Image state
		modifiedImages,

		// Modal type switching
		modalType,
		setModalType,

		// AI Staging Rules settings (product-specific)
		allowCustomBackground,
	};
};
